/*
 * Kategorier på en kampanje med gaver i appen (f.eks. Zakat, Sadaqa). Giveren
 * velger én kategori før betaling, og Innsamlet viser beløp per kategori.
 * Samme regler som serveren: maks 8, 1–40 tegn, ingen like (store/små bokstaver).
 */

export const MAX_CATEGORIES = 8;
export const MAX_CATEGORY_LENGTH = 40;

/** Hurtigvalg under «Legg til kategori». */
export const CATEGORY_SUGGESTIONS = [
  "Zakat",
  "Sadaqa",
  "Fitrana",
  "Lillah",
  "Medlemskontingent",
  "Byggeprosjekt",
];

/** «  Zakat   al-fitr » → «Zakat al-fitr». */
export const normalizeCategory = (name: string) =>
  name.replace(/\s+/g, " ").trim();

const key = (name: string) => normalizeCategory(name).toLocaleLowerCase("nb");

export const hasCategory = (list: string[], name: string) =>
  list.some((c) => key(c) === key(name));

/** Feilmelding for en ny kategori, eller null hvis den kan legges til. */
export const categoryError = (list: string[], name: string) => {
  const clean = normalizeCategory(name);
  if (!clean) return "Skriv navnet på kategorien.";
  if (clean.length > MAX_CATEGORY_LENGTH) {
    return `Maks ${MAX_CATEGORY_LENGTH} tegn per kategori.`;
  }
  if (hasCategory(list, clean)) return "Kategorien finnes allerede.";
  if (list.length >= MAX_CATEGORIES) {
    return `Maks ${MAX_CATEGORIES} kategorier.`;
  }
  return null;
};

/** Legger til (normalisert) hvis gyldig; ellers uendret liste og feilmelding. */
export const addCategory = (list: string[], name: string) => {
  const error = categoryError(list, name);
  return error
    ? { list, error }
    : { list: [...list, normalizeCategory(name)], error: null };
};

export const removeCategory = (list: string[], name: string) =>
  list.filter((c) => key(c) !== key(name));

/** Flytter en kategori ett steg opp (-1) eller ned (+1). */
export const moveCategory = (list: string[], index: number, step: -1 | 1) => {
  const to = index + step;
  if (index < 0 || index >= list.length || to < 0 || to >= list.length) {
    return list;
  }
  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
};

/** Hurtigvalgene som ikke er lagt til ennå. */
export const remainingSuggestions = (list: string[]) =>
  CATEGORY_SUGGESTIONS.filter((s) => !hasCategory(list, s));

/** Hele listen gyldig? Gir første feil, eller undefined. */
export const validateCategories = (list: string[]) => {
  if (list.length > MAX_CATEGORIES) return `Maks ${MAX_CATEGORIES} kategorier.`;
  const seen: string[] = [];
  for (const c of list) {
    const clean = normalizeCategory(c);
    if (!clean || clean.length > MAX_CATEGORY_LENGTH) {
      return `Hver kategori må ha 1–${MAX_CATEGORY_LENGTH} tegn.`;
    }
    if (hasCategory(seen, clean)) return `«${clean}» står to ganger.`;
    seen.push(clean);
  }
  return undefined;
};

/**
 * Kategoriene fra serveren. Mangler feltet (eldre server) eller er det i
 * feil form, blir det en tom liste. Tåler også en JSON-tekst.
 */
export const toCategories = (value: unknown): string[] => {
  let v = value;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(v)) return [];
  return v
    .filter((c): c is string => typeof c === "string")
    .map(normalizeCategory)
    .filter(Boolean);
};
